import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { select } from 'd3-selection';
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import { FileText, Scale, Quote, Brain, Microscope, Gavel } from 'lucide-react';
import './ReasoningFlow.css';

/**
 * Interactive reasoning DAG.
 *
 * The backend emits `{nodes, edges}` derived from verified pipeline data, so
 * every node maps to a real claim, a quoted source, an analysis result or the
 * verdict -- nothing here is model prose.
 *
 * Rendered as plain SVG rather than through a graph library: the backend's layer
 * assignment already determines the layout, so a library would only contribute
 * pan/zoom and edge routing. Doing it directly keeps the render deterministic,
 * keeps the flow animation under our control, and avoids a large dependency.
 */

const TONE = {
  positive: { stroke: '#10b981', text: '#6ee7b7' },
  negative: { stroke: '#ef4444', text: '#fca5a5' },
  caution: { stroke: '#f59e0b', text: '#fcd34d' },
  memory: { stroke: '#8b5cf6', text: '#c4b5fd' },
  neutral: { stroke: '#6366f1', text: '#a5b4fc' },
};

const KIND_ICON = {
  input: FileText,
  claim: Scale,
  evidence: Quote,
  memory: Brain,
  analysis: Microscope,
  verdict: Gavel,
};

const KIND_LABEL = {
  input: 'Claim submitted',
  claim: 'Claim under test',
  evidence: 'Evidence',
  memory: 'Prior memory',
  analysis: 'Analysis',
  verdict: 'Verdict',
};

const NODE_W = 220;
const NODE_H = 96;
const VERDICT_H = 116;
const H_GAP = 34;
const V_GAP = 76;
const PAD = 26;

/** Assign coordinates from each node's layer: one row per layer, centred. */
function computeLayout(nodes) {
  const rows = new Map();
  nodes.forEach((n) => {
    const layer = n.layer ?? 0;
    if (!rows.has(layer)) rows.set(layer, []);
    rows.get(layer).push(n);
  });

  const layers = [...rows.keys()].sort((a, b) => a - b);
  const widest = Math.max(...layers.map((l) => rows.get(l).length), 1);
  const width = widest * NODE_W + (widest - 1) * H_GAP + PAD * 2;

  const placed = [];
  let y = PAD;
  layers.forEach((layer) => {
    const row = rows.get(layer);
    const rowW = row.length * NODE_W + (row.length - 1) * H_GAP;
    const startX = (width - rowW) / 2;
    const rowH = row.some((n) => n.kind === 'verdict') ? VERDICT_H : NODE_H;
    row.forEach((n, i) => {
      placed.push({ ...n, x: startX + i * (NODE_W + H_GAP), y, w: NODE_W, h: rowH });
    });
    y += rowH + V_GAP;
  });

  return { nodes: placed, width, height: y - V_GAP + PAD };
}

/** Vertical cubic bezier from the bottom edge of `a` to the top edge of `b`. */
function edgePath(a, b) {
  const x1 = a.x + a.w / 2;
  const y1 = a.y + a.h;
  const x2 = b.x + b.w / 2;
  const y2 = b.y;
  const dy = Math.max(26, (y2 - y1) / 2);
  return `M${x1},${y1} C${x1},${y1 + dy} ${x2},${y2 - dy} ${x2},${y2}`;
}

const ReasoningFlow = ({ graph }) => {
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const zoomRef = useRef(null);
  const [hovered, setHovered] = useState(null);

  const layout = useMemo(() => computeLayout(graph?.nodes || []), [graph]);

  const edges = useMemo(() => {
    const byId = new Map(layout.nodes.map((n) => [n.id, n]));
    return (graph?.edges || [])
      .map((e) => {
        const from = byId.get(e.source);
        const to = byId.get(e.target);
        if (!from || !to) return null;
        return {
          ...e,
          d: edgePath(from, to),
          mx: (from.x + to.x) / 2 + NODE_W / 2,
          my: (from.y + from.h + to.y) / 2 + 4,
        };
      })
      .filter(Boolean);
  }, [graph, layout]);

  // Drag to pan, buttons to zoom. Wheel zoom is deliberately disabled so the
  // page continues to scroll normally when the cursor is over the diagram.
  useEffect(() => {
    if (!svgRef.current || !viewportRef.current) return undefined;
    const svg = select(svgRef.current);
    const vp = select(viewportRef.current);
    const behaviour = d3zoom()
      .scaleExtent([0.35, 2.2])
      .filter((event) => event.type !== 'wheel')
      .on('zoom', (event) => vp.attr('transform', event.transform.toString()));
    svg.call(behaviour);
    zoomRef.current = { svg, behaviour };
    return () => svg.on('.zoom', null);
  }, [layout]);

  const zoomBy = useCallback((factor) => {
    const z = zoomRef.current;
    if (z) z.svg.transition().duration(220).call(z.behaviour.scaleBy, factor);
  }, []);

  const resetView = useCallback(() => {
    const z = zoomRef.current;
    if (z) z.svg.transition().duration(260).call(z.behaviour.transform, zoomIdentity);
  }, []);

  const connected = useCallback(
    (id) =>
      edges.some(
        (e) =>
          (e.source === hovered && e.target === id) ||
          (e.target === hovered && e.source === id)
      ),
    [edges, hovered]
  );

  if (!graph?.nodes?.length) return null;

  const stats = graph.stats || {};
  const { width, height } = layout;

  return (
    <div className="reasoning-flow-wrapper">
      <div className="reasoning-flow-legend">
        <span className="legend-item supported">Supports</span>
        <span className="legend-item contradicted">Contradicts</span>
        <span className="legend-item unverified">Unverified</span>
        <span className="legend-item memory">Memory (advisory)</span>
        <span className="legend-spacer" />
        <span className="legend-stat">
          {stats.claims || 0} claims · {stats.evidence || 0} evidence
          {stats.memory ? ` · ${stats.memory} recalled` : ''}
        </span>
      </div>

      <div className="reasoning-flow-canvas">
        <svg
          ref={svgRef}
          className="rf-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMin meet"
          role="img"
          aria-label="Reasoning chain"
        >
          <defs>
            {Object.entries(TONE).map(([key, tone]) => (
              <marker
                key={key}
                id={`rf-arrow-${key}`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={tone.stroke} />
              </marker>
            ))}
          </defs>

          <g ref={viewportRef}>
            {/* Edges are drawn first so node cards paint over the line ends. */}
            {edges.map((edge) => {
              const tone = TONE[edge.tone] || TONE.neutral;
              const active = hovered === edge.source || hovered === edge.target;
              return (
                <g
                  key={edge.id}
                  className={`rf-edge ${edge.dashed ? 'dashed' : 'solid'}`}
                  opacity={hovered && !active ? 0.15 : 1}
                >
                  <path
                    d={edge.d}
                    fill="none"
                    stroke={tone.stroke}
                    strokeWidth={edge.dashed ? 1.5 : 2}
                    strokeDasharray={edge.dashed ? '6 5' : '9 7'}
                    strokeLinecap="round"
                    markerEnd={`url(#rf-arrow-${edge.tone || 'neutral'})`}
                    opacity={edge.dashed ? 0.85 : 1}
                  />
                  {edge.label && (
                    <text
                      className="rf-edge-label"
                      x={edge.mx}
                      y={edge.my}
                      fill={tone.text}
                      textAnchor="middle"
                    >
                      {edge.label}
                    </text>
                  )}
                </g>
              );
            })}

            {layout.nodes.map((node) => {
              const tone = TONE[node.tone] || TONE.neutral;
              const Icon = KIND_ICON[node.kind] || FileText;
              const isVerdict = node.kind === 'verdict';
              const dimmed = hovered && hovered !== node.id && !connected(node.id);
              return (
                <g
                  key={node.id}
                  className={`rf-node ${isVerdict ? 'verdict' : ''}`}
                  transform={`translate(${node.x},${node.y})`}
                  opacity={dimmed ? 0.22 : 1}
                  onMouseEnter={() => setHovered(node.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <rect
                    className="rf-node-box"
                    width={node.w}
                    height={node.h}
                    rx="10"
                    fill="#1f2937"
                    stroke={tone.stroke}
                    strokeWidth={isVerdict ? 2 : 1.2}
                  />
                  <rect width="3" height={node.h} rx="1.5" fill={tone.stroke} />

                  <foreignObject x="11" y="9" width={node.w - 22} height={node.h - 16}>
                    <div className="rf-node-body">
                      <div className="rf-node-head" style={{ color: tone.text }}>
                        <Icon size={11} />
                        <span>{KIND_LABEL[node.kind] || node.kind}</span>
                        {node.meta?.confidence != null && (
                          <span className="rf-node-pct">
                            {Math.round(node.meta.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      <div className={`rf-node-label ${isVerdict ? 'big' : ''}`}>
                        {node.label}
                      </div>
                      {isVerdict ? (
                        <div className="rf-tally">
                          <span className="t supported">{node.meta?.supported ?? 0}</span>
                          <span className="t contradicted">{node.meta?.contradicted ?? 0}</span>
                          <span className="t unverified">{node.meta?.unverified ?? 0}</span>
                        </div>
                      ) : (
                        node.detail && <div className="rf-node-detail">{node.detail}</div>
                      )}
                    </div>
                  </foreignObject>
                  <title>{node.detail ? `${node.label}\n\n${node.detail}` : node.label}</title>
                </g>
              );
            })}
          </g>
        </svg>

        <div className="rf-controls">
          <button type="button" onClick={() => zoomBy(1.25)} aria-label="Zoom in">+</button>
          <button type="button" onClick={() => zoomBy(0.8)} aria-label="Zoom out">&minus;</button>
          <button type="button" onClick={resetView} aria-label="Reset view">&#8624;</button>
        </div>
      </div>
    </div>
  );
};

export default ReasoningFlow;
